<template>
  <div :class="style.content">
    <div :class="style.tabs">
      <TabPanel
        text="Настройки"
        :active="activeSection === 'Настройки'"
        @click="setActiveSection('Настройки')"
      />

      <TabPanel
        text="Контент"
        :active="activeSection === 'Контент'"
        @click="setActiveSection('Контент')"
      />
    </div>

    <div v-if="activeSection === 'Настройки'">
      <div :class="style.block">
        <SelectBase
          comment="Тип блока"
          :text="contactsTypeText"
          :items="contactsTypeOptions"
          @select="setContactsType"
        />
      </div>
    </div>

    <div v-if="activeSection === 'Контент'">
      <div :class="mobileVersion ? style.mobileContent : null">
        <AccordionBase
          v-if="isShowPhones"
          :class="style.accordion"
          :classHeader="[style.accordionHeader]"
          :classContent="!phones.length ? [style.noBody] : []"
          text="Телефоны"
          showChevronWithActions
        >
          <template #actions>
            <div :class="style.accordionContact">
              <ButtonIcon
                :icon="addIcon"
                @click.stop="addPhone"
                variant="ghost"
                size="28"
              />
            </div>
          </template>

          <div v-if="phones.length" :class="style.inputs">
            <InputStyle
              :classes="[style.input]"
              v-for="(phone, phoneIndex) in phones"
              :key="`phone-${phoneIndex}`"
              :modelValue="phone"
              size="M"
              placeholder="+7 999 123-45-67"
              type="tel"
              @update:modelValue="setPhone(phoneIndex, $event)"
            >
              <template #actions>
                <ActionMenu
                  v-model="phoneActionMenuOpen[phoneIndex]"
                  :actions="getPhoneActions(phoneIndex)"
                  :teleport="true"
                  :closeOnScroll="true"
                  :closeOnResize="true"
                  placement="bottom-end"
                  :offset="8"
                >
                  <template #trigger="{toggle}">
                    <ButtonIcon
                      :icon="moreIcon"
                      @click.stop="toggle"
                      variant="ghost"
                    />
                  </template>
                </ActionMenu>
              </template>
            </InputStyle>
          </div>
        </AccordionBase>

        <AccordionBase
          v-if="isShowAddresses"
          :class="[
            style.accordion,
            style.details,
            mobileVersion ? style.mobileAddress : null
          ]"
          :classHeader="[style.accordionHeader]"
          :classContent="!addresses.length ? [style.noBody] : []"
          text="Адреса"
          showChevronWithActions
        >
          <template #actions>
            <div :class="style.accordionContact">
              <ButtonIcon
                :icon="addIcon"
                @click.stop="addAddress"
                variant="ghost"
                size="28"
              />
            </div>
          </template>

          <div v-if="addresses.length" :class="style.inputs">
            <InputStyle
              :classes="[style.input]"
              v-for="(address, addressIndex) in addresses"
              :key="`address-${addressIndex}`"
              :modelValue="address"
              placeholder="Введите адрес"
              size="M"
              @update:modelValue="setAddress(addressIndex, $event)"
            >
              <template #actions>
                <ActionMenu
                  v-model="addressActionMenuOpen[addressIndex]"
                  :actions="getAddressActions(addressIndex)"
                  :teleport="true"
                  :closeOnScroll="true"
                  :closeOnResize="true"
                  placement="bottom-end"
                  :offset="8"
                >
                  <template #trigger="{toggle}">
                    <ButtonIcon
                      :icon="moreIcon"
                      @click.stop="toggle"
                      variant="ghost"
                    />
                  </template>
                </ActionMenu>
              </template>
            </InputStyle>
          </div>
        </AccordionBase>
      </div>

      <InputStyle
        v-if="!mobileVersion"
        :modelValue="companyName"
        description="Реквизиты"
        placeholder="Введите реквизиты"
        size="M"
        @update:modelValue="setCompanyName"
      />
    </div>
  </div>
</template>

<script lang="ts">
import {defineComponent, markRaw} from "vue";
import style from "./ContactsSettingComponent.module.scss";
import {modifyQuiz} from "@/page/quiz/scriptQuiz";
import {ContactsType, Quiz} from "@/page/quiz/typeQuiz";

import AccordionBase from "@/elements/UI/accordion/AccordionBase.vue";
import InputStyle from "@/elements/UI/input/Style/InputStyle.vue";
import GhostButton from "@/elements/UI/button/ButtonStyle/Default/ghost/GhostButton.vue";
import ActionMenu from "@/elements/UI/popup/ActionMenu/ActionMenu.vue";
import SelectBase from "@/elements/UI/select/SelectBase.vue";
import TabPanel from "@/elements/UI/tabs/TabPanel/TabPanel.vue";

import addIcon from "$images/icons/button/Name=Add, Size=16, Tone=Default.svg";
import moreIcon from "$images/icons/button/Name=More, Size=16, Tone=Default.svg";
import chevronUpIcon from "$images/icons/button/Name=Chevron-up, Size=16, Tone=Default.svg";
import chevronDownIcon from "$images/icons/button/Name=Chevron-down, Size=16, Tone=Default.svg";
import deleteIcon from "$images/icons/button/Name=Delete, Size=16, Tone=Error.svg";
import ButtonIcon from "@/elements/UI/button/ButtonIcon/ButtonIcon.vue";
import GhostButtonError from "@/elements/UI/button/ButtonStyle/Error/ghost/GhostButtonError.vue";

type ContactsTypeOption = SelectItem & {
  text: string;
  value: ContactsType;
};

export default defineComponent({
  name: "ContactsSettingComponent",

  components: {
    ButtonIcon,
    SelectBase,
    TabPanel,
    ActionMenu,
    GhostButton,
    InputStyle,
    AccordionBase
  },

  data() {
    return {
      style,
      modifyQuiz,
      addIcon,
      moreIcon,

      activeSection: "Настройки" as SelectedPanel,

      phoneActionMenuOpen: [] as boolean[],
      addressActionMenuOpen: [] as boolean[],

      contactsTypeOptions: [
        {
          text: "Только адрес",
          value: "onlyAddress",
          variant: "tab"
        },
        {
          text: "Телефон + адрес",
          value: "both",
          variant: "tab"
        },
        {
          text: "Только телефон",
          value: "onlyPhone",
          variant: "tab"
        }
      ] as ContactsTypeOption[]
    };
  },

  mounted() {
    this.setDefaultContactsType();
  },

  methods: {
    setActiveSection(name: SelectedPanel): void {
      this.activeSection = name;
    },

    getCurrentContactsType(): ContactsType {
      const currentType = this.modifyQuiz("get", "", [
        "styleContacts",
        "type"
      ]) as ContactsType | undefined;

      if (
        currentType === "onlyAddress" ||
        currentType === "onlyPhone" ||
        currentType === "both"
      ) {
        return currentType;
      }

      return "both";
    },

    setDefaultContactsType(): void {
      const currentType = this.modifyQuiz("get", "", [
        "styleContacts",
        "type"
      ]) as ContactsType | undefined;

      if (
        currentType === "onlyAddress" ||
        currentType === "onlyPhone" ||
        currentType === "both"
      ) {
        return;
      }

      this.modifyQuiz("set", "both", ["styleContacts", "type"]);
    },

    setContactsType(payload: SelectBasePayload): void {
      const value = payload.value;

      if (
        value !== "onlyAddress" &&
        value !== "onlyPhone" &&
        value !== "both"
      ) {
        return;
      }

      this.modifyQuiz("set", value, ["styleContacts", "type"]);
    },

    setCompanyName(value: string): void {
      this.modifyQuiz("set", value, ["сompanyName"]);
    },

    addPhone(): void {
      const phones = [...this.phones];

      phones.push("");

      this.modifyQuiz("set", phones, ["phone"]);
    },

    addAddress(): void {
      const addresses = [...this.addresses];

      addresses.push("");

      this.modifyQuiz("set", addresses, ["address"]);
    },

    setPhone(phoneIndex: number, value: string): void {
      const phones = [...this.phones];

      phones[phoneIndex] = value;

      this.modifyQuiz("set", phones, ["phone"]);
    },

    setAddress(addressIndex: number, value: string): void {
      const addresses = [...this.addresses];

      addresses[addressIndex] = value;

      this.modifyQuiz("set", addresses, ["address"]);
    },

    movePhoneUp(phoneIndex: number): void {
      if (phoneIndex <= 0) {
        return;
      }

      const phones = [...this.phones];

      [phones[phoneIndex - 1], phones[phoneIndex]] = [
        phones[phoneIndex],
        phones[phoneIndex - 1]
      ];

      this.modifyQuiz("set", phones, ["phone"]);
      this.closePhoneActionMenu(phoneIndex);
    },

    movePhoneDown(phoneIndex: number): void {
      if (phoneIndex >= this.phones.length - 1) {
        return;
      }

      const phones = [...this.phones];

      [phones[phoneIndex + 1], phones[phoneIndex]] = [
        phones[phoneIndex],
        phones[phoneIndex + 1]
      ];

      this.modifyQuiz("set", phones, ["phone"]);
      this.closePhoneActionMenu(phoneIndex);
    },

    moveAddressUp(addressIndex: number): void {
      if (addressIndex <= 0) {
        return;
      }

      const addresses = [...this.addresses];

      [addresses[addressIndex - 1], addresses[addressIndex]] = [
        addresses[addressIndex],
        addresses[addressIndex - 1]
      ];

      this.modifyQuiz("set", addresses, ["address"]);
      this.closeAddressActionMenu(addressIndex);
    },

    moveAddressDown(addressIndex: number): void {
      if (addressIndex >= this.addresses.length - 1) {
        return;
      }

      const addresses = [...this.addresses];

      [addresses[addressIndex + 1], addresses[addressIndex]] = [
        addresses[addressIndex],
        addresses[addressIndex + 1]
      ];

      this.modifyQuiz("set", addresses, ["address"]);
      this.closeAddressActionMenu(addressIndex);
    },

    deletePhone(phoneIndex: number): void {
      const phones = [...this.phones];

      phones.splice(phoneIndex, 1);

      this.modifyQuiz("set", phones, ["phone"]);
      this.closePhoneActionMenu(phoneIndex);
    },

    deleteAddress(addressIndex: number): void {
      const addresses = [...this.addresses];

      addresses.splice(addressIndex, 1);

      this.modifyQuiz("set", addresses, ["address"]);
      this.closeAddressActionMenu(addressIndex);
    },

    closePhoneActionMenu(phoneIndex: number): void {
      this.phoneActionMenuOpen[phoneIndex] = false;
    },

    closeAddressActionMenu(addressIndex: number): void {
      this.addressActionMenuOpen[addressIndex] = false;
    },

    getPhoneActions(phoneIndex: number): ListActionItem[] {
      return [
        {
          text: "Переместить вверх",
          icon: chevronUpIcon,
          disabled: phoneIndex <= 0,
          variant: "button",
          handler: () => this.movePhoneUp(phoneIndex)
        },
        {
          text: "Переместить вниз",
          icon: chevronDownIcon,
          disabled: phoneIndex >= this.phones.length - 1,
          variant: "button",
          handler: () => this.movePhoneDown(phoneIndex)
        },
        {
          text: "Удалить",
          icon: deleteIcon,
          variant: "button",
          buttonComponent: markRaw(GhostButtonError),
          handler: () => this.deletePhone(phoneIndex)
        }
      ];
    },

    getAddressActions(addressIndex: number): ListActionItem[] {
      return [
        {
          text: "Переместить вверх",
          icon: chevronUpIcon,
          disabled: addressIndex <= 0,
          variant: "button",
          handler: () => this.moveAddressUp(addressIndex)
        },
        {
          text: "Переместить вниз",
          icon: chevronDownIcon,
          disabled: addressIndex >= this.addresses.length - 1,
          variant: "button",
          handler: () => this.moveAddressDown(addressIndex)
        },
        {
          text: "Удалить",
          icon: deleteIcon,
          variant: "button",
          buttonComponent: markRaw(GhostButtonError),
          handler: () => this.deleteAddress(addressIndex)
        }
      ];
    }
  },

  computed: {
    quiz(): Quiz {
      return this.$quizStore.getQuiz;
    },

    phones(): string[] {
      const value = this.modifyQuiz("get", "", ["phone"]);

      return Array.isArray(value) ? value : [];
    },

    addresses(): string[] {
      const value = this.modifyQuiz("get", "", ["address"]);

      return Array.isArray(value) ? value : [];
    },

    companyName(): string {
      const value = this.modifyQuiz("get", "", ["сompanyName"]);

      return typeof value === "string" ? value : "";
    },

    currentContactsType(): ContactsType {
      return this.getCurrentContactsType();
    },

    contactsTypeText(): string {
      const currentOption = this.contactsTypeOptions.find((option) => {
        return option.value === this.currentContactsType;
      });

      return currentOption?.text || "Телефоны + адреса";
    },

    isOnlyAddressStyleContacts(): boolean {
      return this.currentContactsType === "onlyAddress";
    },

    isOnlyPhoneStyleContacts(): boolean {
      return this.currentContactsType === "onlyPhone";
    },

    isBothStyleContacts(): boolean {
      return this.currentContactsType === "both";
    },

    isShowPhones(): boolean {
      return this.isOnlyPhoneStyleContacts || this.isBothStyleContacts;
    },

    isShowAddresses(): boolean {
      return this.isOnlyAddressStyleContacts || this.isBothStyleContacts;
    },

    mobileVersion(): boolean {
      return this.selectedVersion === "Мобильная";
    },

    selectedVersion(): string {
      return this.$quizStore.getSelectedVersion;
    }
  },

  watch: {
    quiz: {
      handler() {
        this.setDefaultContactsType();
      },
      deep: true
    }
  }
});
</script>
